import apiClient from './api';
import { saveBlob } from './exportService';

// Reports area (docs/design/contracts.md §7 "Reports and exports", "Lineage and PSTN", plus the A016 routes:
// reports/exports/recent, export/transcripts, pstn/reconciliation/invoiced, pstn/reconciliation/days).

const filenameFrom = (contentDisposition, fallback) => {
  if (!contentDisposition) return fallback;
  const utf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = contentDisposition.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
};

/** The `{ message }` of a failed request, also when the response was requested as a blob. */
export async function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed?.message) return parsed.message;
    } catch (e) {
      // Not JSON: use the fallback below.
    }
  } else if (data?.message) {
    return data.message;
  }
  if (error?.response?.status === 403) return 'You do not have permission to do this';
  return fallback;
}

async function download(request, fallbackName) {
  try {
    const response = await request();
    const filename = filenameFrom(response.headers['content-disposition'], fallbackName);
    saveBlob(response.data, filename);
    return { success: true, filename, blob: response.data };
  } catch (error) {
    return { success: false, error: await errorMessage(error, `Couldn't export ${fallbackName}`) };
  }
}

/** Repeated keys as comma-separated lists, as the API's AnalyticsFilter parser accepts. */
export function toQuery(params) {
  const out = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      if (value.length) out[key] = value.join(',');
    } else {
      out[key] = value;
    }
  });
  return out;
}

export const reportsAPI = {
  getDatasets: async () => (await apiClient.get('/reports/datasets')).data,
  getSaved: async () => (await apiClient.get('/reports/saved')).data,
  createSaved: async (report) => (await apiClient.post('/reports/saved', report)).data,
  updateSaved: async (id, report) => (await apiClient.put(`/reports/saved/${encodeURIComponent(id)}`, report)).data,
  deleteSaved: async (id) => apiClient.delete(`/reports/saved/${encodeURIComponent(id)}`),
  preview: async (query) => (await apiClient.post('/reports/preview', query)).data,
  run: (query, format) =>
    download(() => apiClient.post('/reports/run', { ...query, format }, { responseType: 'blob' }), `report.${format}`),
  runSaved: (id, format) =>
    download(() => apiClient.get(`/reports/saved/${encodeURIComponent(id)}/run`, { params: { format }, responseType: 'blob' }), `report.${format}`),
  getBoardCommunities: async (month) => (await apiClient.get('/reports/community-summary/communities', { params: { month } })).data,
  communitySummaryBlob: async ({ communityId, month, format = 'pdf', includeCallerNames = false, sections }) => {
    const response = await apiClient.get('/reports/community-summary', {
      params: { communityId, month, format, includeCallerNames, sections: sections || undefined, preview: true },
      responseType: 'blob',
    });
    return { blob: response.data, filename: filenameFrom(response.headers['content-disposition'], `board-summary-${month}.${format}`) };
  },
  communitySummary: (params) =>
    download(
      () =>
        apiClient.get('/reports/community-summary', {
          params: { format: 'pdf', includeCallerNames: false, ...params },
          responseType: 'blob',
        }),
      `board-summary-${params.month}.${params.format || 'pdf'}`
    ),
  exportDataset: (dataset, params, format) =>
    download(
      () => apiClient.get(`/export/datasets/${encodeURIComponent(dataset)}`, { params: toQuery({ ...params, format }), responseType: 'blob' }),
      `${dataset.toLowerCase()}.${format}`
    ),
  pageExport: (route, params) =>
    download(() => apiClient.get(`/${route.replace(/^\/+/, '')}`, { params, responseType: 'blob' }), `${route.split('/').pop()}.${params.format || 'csv'}`),
  exportTranscripts: (params) =>
    download(() => apiClient.get('/export/transcripts', { params: toQuery({ ...params, format: 'zip' }), responseType: 'blob' }), 'transcripts.zip'),
  getSchedules: async () => (await apiClient.get('/reports/schedules')).data,
  createSchedule: async (schedule) => (await apiClient.post('/reports/schedules', schedule)).data,
  updateSchedule: async (id, schedule) => (await apiClient.put(`/reports/schedules/${encodeURIComponent(id)}`, schedule)).data,
  deleteSchedule: async (id) => apiClient.delete(`/reports/schedules/${encodeURIComponent(id)}`),
  runScheduleNow: async (id) => (await apiClient.post(`/reports/schedules/${encodeURIComponent(id)}/run-now`)).data,
  getRecipientGroups: async () => (await apiClient.get('/reports/recipient-groups')).data,
  getDeliveryStatus: async () => (await apiClient.get('/reports/delivery-status')).data,
  getRecentExports: async () => (await apiClient.get('/reports/exports/recent')).data,
  getActiveTaxonomy: async () => (await apiClient.get('/config/taxonomy/active')).data,
};

export const pstnAPI = {
  getReconciliation: async (month) => (await apiClient.get('/pstn/reconciliation', { params: { month } })).data,
  runReconciliation: async (month) => (await apiClient.post('/pstn/reconciliation/run', null, { params: { month } })).data,
  getCalls: async (month, matched, page = 1, pageSize = 500) =>
    (await apiClient.get('/pstn/calls', { params: { month, matched, page, pageSize } })).data,
  getDays: async (month) => (await apiClient.get('/pstn/reconciliation/days', { params: { month } })).data,
  getInvoiceMark: async (month) => (await apiClient.get('/pstn/reconciliation/invoiced', { params: { month } })).data,
  markInvoiced: async (month, basis) =>
    (await apiClient.post('/pstn/reconciliation/invoiced', null, { params: { month, basis } })).data,
  unmarkInvoiced: async (month) => apiClient.delete('/pstn/reconciliation/invoiced', { params: { month } }),
  exportMonth: (month) =>
    download(
      () => apiClient.get('/export/datasets/PstnReconciliation', { params: { month, format: 'xlsx' }, responseType: 'blob' }),
      `pstn-reconciliation-${month}.xlsx`
    ),
};
